/* =============================================================================
   VIEWS / ADMIN / RECEIPT — Zentraler Bon-Renderer (modern + finanzamt-konform)
   =============================================================================
   Gemeinsamer Renderer für zwei Modes:
     - 'day-close'  → Z-Bon (Tagesabschluss)
     - 'order'      → Kassenbon einer Einzelbestellung

   Belegpflichtangaben (gemäß §14 UStG Kleinbetragsrechnung + §6 KassenSichV):
     • Vollständiger Name & Anschrift des leistenden Unternehmers
     • Datum + Uhrzeit der Ausstellung
     • Menge + Art der Lieferung/Leistung
     • Bruttobetrag + MwSt-Splits (Satz + Betrag) je Steuersatz
     • Steuernummer ODER USt-IdNr
     • Eindeutige Belegnummer (Rechnungs- bzw. Z-Bon-Nummer)
     • TSE-Block (KassenSichV) — aktuell Platzhalter mit Hinweis, dass
       TSE-Anbindung im Aufbau ist. Spätere Cloud-TSE (z.B. fiskaly,
       Epson, Swissbit) trägt hier Transaktions-Nr + Signaturzähler ein.

   Layout: zweistufig — Web-Card (modern, lesbar, Brand-Akzent) und
   @media print → 80mm monospace (bewährt aus Session 13).
   ============================================================================ */

import { htmlEscape, todayISO, dateToISO, displayGuestName } from '../../utils/helpers.js';
import { getBrandLogoSrc, DEFAULT_LOGO_URL } from '../../core/branding.js';
import { generateQrSvg } from '../../utils/qr-code.js';
import { deliveryFeeInfo as deliveryFeeReceiptInfo, grossPerRateFromOrder } from '../../core/tse-amounts.js';

// S241 / HF-10 — Die Pro-Satz-Brutto-Logik lebt seit diesem Cut im Core
// (core/tse-amounts.js), damit Bon, TSE-Signatur und Tagesabschluss dieselbe
// Quelle benutzen. Der alte Name bleibt für bestehende Importeure erhalten
// (orders.js zieht `deliveryFeeReceiptInfo` für die Gebühren-Positionszeile).
export { deliveryFeeReceiptInfo };

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Format-Helpers
// -----------------------------------------------------------------------------

function centsToEur(cents) {
  const v = (Number(cents) || 0) / 100;
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDateDE(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtTime(t) {
  return String(t || '').slice(0, 5);
}

function fmtNow() {
  return new Date().toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function snapshotItems(snap) {
  if (!snap) return [];
  if (Array.isArray(snap)) return snap;
  if (Array.isArray(snap.items)) return snap.items;
  return [];
}

const PAYMENT_LABEL = {
  cash:   'Bar',
  card:   'EC / Kreditkarte',
  stripe: 'Online (Stripe)',
  '':     'Unbezahlt'
};

// Phase 10a.2: Stornobeleg-Begründungen — Dropdown-Werte aus
// orders_correction_reason_code_check. Mapping für Anzeige im Bon.
export const CORRECTION_REASON_LABEL = {
  wrong_input:        'Falsche Eingabe',
  customer_complaint: 'Kunde reklamiert',
  goodwill:           'Kulanz',
  wrong_position:     'Falsche Position',
  other:              'Sonstiges'
};

// -----------------------------------------------------------------------------
// Stammdaten aus Settings — werden vom Bon-Header gerendert
// -----------------------------------------------------------------------------

/**
 * Betriebsangaben für den Belegkopf.
 *
 * S254 — `override` erlaubt es, sie von aussen mitzugeben. Der Gast am Tisch
 * hat keinen `window.App.storage`-State (die Einstellungen des Betriebs sind
 * für ihn nicht lesbar, und das soll auch so bleiben); er bekommt dieselben
 * Angaben über `guest_receipt_view`. Ohne `override` bleibt alles beim Alten —
 * jeder Cockpit-Aufrufer ist unverändert.
 */
function getCompanyHeader(override) {
  if (override && typeof override === 'object') {
    const o = override;
    return {
      name:      String(o.name || o.legalName || o.brand || 'Grill & Green').trim(),
      brand:     String(o.brand || o.name || 'Grill & Green').trim(),
      street:    String(o.street || '').trim(),
      zip:       String(o.zip || '').trim(),
      city:      String(o.city || '').trim(),
      phone:     String(o.phone || '').trim(),
      email:     String(o.email || '').trim(),
      ustId:     String(o.ustId || '').trim(),
      taxNumber: String(o.taxNumber || '').trim()
    };
  }
  const s = getState().settings || {};
  const r = s.restaurant || {};
  return {
    name:       (r.legalName || s.restaurantName || 'Grill & Green').trim(),
    brand:      (s.restaurantName || 'Grill & Green').trim(),
    street:     (r.street || '').trim(),
    zip:        (r.zip || '').trim(),
    city:       (r.city || '').trim(),
    phone:      (s.restaurantPhone || '').trim(),
    email:      (s.restaurantEmail || '').trim(),
    ustId:      (r.ustId || '').trim(),
    taxNumber:  (r.taxNumber || '').trim()
  };
}

function companyAddressLine(c) {
  const parts = [];
  if (c.street) parts.push(c.street);
  const placeparts = [c.zip, c.city].filter(Boolean).join(' ');
  if (placeparts) parts.push(placeparts);
  return parts.join(' · ');
}

function taxIdLine(c) {
  if (c.ustId)     return 'USt-IdNr: ' + c.ustId;
  if (c.taxNumber) return 'Steuernr: ' + c.taxNumber;
  return '— USt-IdNr / Steuernr noch nicht hinterlegt —';
}

// -----------------------------------------------------------------------------
// MODE 'order' — Kassenbon für eine einzelne Bestellung
// -----------------------------------------------------------------------------

/**
 * Aggregiert die Daten einer Order zu einem Bon-Datensatz.
 * Order-Schema entspricht der Frontend-Camelcase-Variante aus storage.js.
 */
export function computeOrderReceipt(order) {
  if (!order) return null;
  const items = snapshotItems(order.preorderSnapshot);

  // MwSt-Splits aus den Cent-Spalten der Order (sind serverseitig schon
  // korrekt berechnet, nicht hier nochmal aus Items rechnen — Rundungs-
  // unterschiede).
  const grossCents = Number(order.totalGrossCents) || 0;
  // Hinweis: der storage.js-Mapper liefert die Spalten als `tax7Cents` /
  // `tax19Cents` (nicht `tax7AmountCents`). Beide Varianten defensiv lesen,
  // damit alte Caller, die das Roh-Order-Objekt durchreichen, auch greifen.
  const tax7Cents  = Number(order.tax7Cents)  || Number(order.tax7AmountCents)  || 0;
  const tax19Cents = Number(order.tax19Cents) || Number(order.tax19AmountCents) || 0;
  const netCents   = Number(order.subtotalNetCents) || (grossCents - tax7Cents - tax19Cents);

  // Netto-Aufteilung pro Steuersatz (für die VK-Pflicht-Splits).
  // S145 FIX 2 (Korrektur des S143c-Reconcile): Die Bemessungsgrundlage pro Satz
  // lässt sich NICHT aus den gerundeten Steuerbeträgen rekonstruieren —
  // round(tax*100/satz) + Residual-auf-größeren trifft nur die SUMME, nicht den
  // echten item-abgeleiteten Pro-Satz-Wert. S143c-Live-Befund (gemischt 7/19 +
  // Liefergebühr): net7 1193 statt 1196, net19 700 statt 697; der 7%-Block kam
  // auf 12,77 statt 12,80 € (Bemessungsgrundlage je 3 Cent verschoben).
  //
  // Korrekt: Brutto pro Satz aus den Positionen (+ Liefergebühr, eigener Satz)
  // summieren und die gespeicherte (TSE-konsistente) Steuer abziehen →
  // net_satz = brutto_satz − steuer_satz. Das ist exakt, je Satz net+tax ==
  // brutto, und net7+net19 == netCents.
  //
  // Defensiver Fallback: Reconcilen die Item-Brutti NICHT mit dem Order-Brutto
  // (Snapshot fehlt/unvollständig — z. B. Cockpit-Orders ohne preorder_snapshot,
  // Memory feedback_gg_mail_items_from_order_items), nutzen wir die alte
  // aggregat-basierte Schätzung (Summe bleibt korrekt). Stornobeleg: grossCents/
  // tax negativ, Item-Brutto positiv → Vorzeichen anhand grossCents drehen.
  //
  // S241 / HF-10: Diese Aufteilung kam bis hierher aus einem eigenen Inline-Block.
  // Sie lebt jetzt in `core/tse-amounts.js` und füllt DIESELBE Quelle wie der
  // TSE-Body — vorher rechnete die Edge-Function die Pro-Satz-Beträge aus der
  // gerundeten Steuer zurück, sodass der signierte QR 3–7 ct neben dem Bon lag.
  // Zusätzlich deckt der Helper den Rabatt-Fall exakt ab (Positionen tragen das
  // VOR-Rabatt-Brutto, `total_gross_cents` das NACH-Rabatt-Brutto).
  const _perRate = grossPerRateFromOrder(order, { items });
  const net7Cents  = _perRate.gross7Cents  - tax7Cents;
  const net19Cents = _perRate.gross19Cents - tax19Cents;

  return {
    mode:      'order',
    order,
    items,
    // Brutto je Satz — für den TSE-Body und die Bon-Steuertabelle dieselbe Quelle.
    gross7Cents:  _perRate.gross7Cents,
    gross19Cents: _perRate.gross19Cents,
    gross0Cents:  _perRate.gross0Cents,
    grossPerRateSource: _perRate.source,
    grossCents,
    netCents,
    net7Cents,
    net19Cents,
    tax7Cents,
    tax19Cents,
    invoiceNumber: order.invoiceNumber || null,
    orderNumber:   order.orderNumber   || order.id,
    paymentMethod: (order.paymentStatus === 'paid' || order.paymentStatus === 'refunded')
                     ? (order.paymentMethod || '') : '',
    paidAt:        order.paidAt || null,
    // Phase 10a.2 — Stornobeleg-Markierung
    isCorrection:        !!order.isCorrection,
    correctsOrderId:     order.correctsOrderId || null,
    correctionReasonCode: order.correctionReasonCode || null,
    correctionReasonText: order.correctionReasonText || null
  };
}

// S143 / Ticket 1 — `deliveryFeeReceiptInfo` (Liefergebühr als Beleg-Position)
// ist seit S241 `deliveryFeeInfo` in core/tse-amounts.js: Bon UND TSE-Signatur
// brauchen die Gebühr in ihrem Steuersatz, also gehört sie in den Core. Der
// Re-Export oben hält den alten Namen für orders.js am Leben.

// -----------------------------------------------------------------------------
// MODE 'day-close' — Z-Bon (eine Aggregation für einen Tag)
// -----------------------------------------------------------------------------

/**
 * Liefert eine fortlaufende Z-Bon-Sequenznummer. Default = der Tagesindex
 * (laufende Tagesnummer im Jahr) — robust ohne extra DB-Schema. Sobald
 * der User einen echten Z-Bon-Zähler will, kann das in
 * settings.zBonNextNumber persistiert werden.
 */
function computeZBonSequence(dateIso) {
  if (!dateIso) return '';
  const d = new Date(dateIso + 'T00:00:00');
  if (isNaN(d.getTime())) return dateIso;
  const start = new Date(d.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((d - start) / 86400000) + 1;
  return d.getFullYear() + '-' + String(dayOfYear).padStart(3, '0');
}

/**
 * Pure-Helper: berechnet den Z-Bon aus einer **injizierten** Orders-Liste.
 * Wird sowohl vom sync-Pfad (Cache-Read über `computeDayCloseReceipt`) als
 * auch vom async-Pfad (DB-Fresh-Read über `computeDayCloseReceiptFresh`)
 * genutzt — Memory `feedback_gg_export_fresh_db` (Session 43, M-11):
 * historische Z-Bons müssen aus DB statt aus dem 30-Tage-Cache gelesen
 * werden, sonst sind alle Beträge falsch für Daten älter als 30 Tage.
 *
 * @param {string} dateIso
 * @param {Array} ordersIn - vollständige Orders-Liste (entweder Cache oder DB-Fresh)
 */
export function _computeDayCloseReceiptFromOrders(dateIso, ordersIn, schwundRows, receiptsIn, batchesIn) {
  const orders = Array.isArray(ordersIn) ? ordersIn : [];
  const all = orders.filter(o => dayCloseBookingDate(o) === dateIso);
  // S137 — Teilbelege auf den Buchungstag projizieren (created_at = Vereinnahmung,
  // Europe/Berlin via dateToISO), analog zur Order-Buchungstag-Logik.
  const receipts = (Array.isArray(receiptsIn) ? receiptsIn : [])
    .filter(r => dateToISO(r.createdAt) === dateIso);
  // S242 — Korrektur-Belege genauso: created_at ist der Erstattungszeitpunkt.
  const batches = (Array.isArray(batchesIn) ? batchesIn : [])
    .filter(b => dateToISO(b.createdAt) === dateIso);
  return _computeDayCloseReceiptDetails(dateIso, all, schwundRows, receipts, batches);
}

/**
 * GoBD-Buchungstag einer Order für die Z-Bon-Tagesaggregation (S92, Befund B).
 *
 * Eine **bezahlte** (oder erstattete) Order zählt zum Tag der **Vereinnahmung** —
 * also dem Zahl-/TSE-Signaturzeitpunkt (`paidAt`), denn dann fließt das Geld in
 * die Kasse (Bareinnahme-Logik nach GoBD / AEAO zu § 146 AO). Eine heute bar
 * kassierte Vorbestellung mit Abholung morgen gehört damit in den **heutigen**
 * Z-Bon, nicht in den des Abholtags.
 *
 * Eine **noch unbezahlte** Order ist keine Kasseneinnahme — sie bleibt operativ
 * ihrem **Abholtag** (`pickupDate`) zugeordnet, bis sie bezahlt wird, und wandert
 * dann automatisch auf den Zahltag.
 *
 * `paidAt` ist ein UTC-Timestamp; `dateToISO` projiziert ihn DST-fest auf den
 * Kalendertag in der Restaurant-Timezone (Europe/Berlin), damit Zahlungen kurz
 * vor/nach Mitternacht dem korrekten Kassentag zugeordnet werden.
 *
 * @param {object} o gemappte Order (camelCase)
 * @returns {string} 'YYYY-MM-DD' Buchungstag
 */
export function dayCloseBookingDate(o) {
  if (!o) return '';
  const isPaid = o.paymentStatus === 'paid' || o.paymentStatus === 'refunded';
  if (isPaid && o.paidAt) {
    const iso = dateToISO(o.paidAt);
    if (iso) return iso;
  }
  return o.pickupDate || '';
}

/**
 * Async-Variante: liest die Orders für `dateIso` direkt aus der DB via
 * `storage.orders.listForRange`. Schließt M-11 für Z-Bons älter als
 * 30 Tage (Cache-Cutoff). Fällt bei fehlendem `listForRange` zurück auf
 * den Cache (mit `console.warn` analog `archive.js`).
 *
 * @param {string} dateIso
 * @returns {Promise<object>} gleicher Vertrag wie computeDayCloseReceipt
 */
export async function computeDayCloseReceiptFresh(dateIso) {
  const storage = window.App && window.App.storage && window.App.storage.orders;
  // S92 / Befund B: Bevorzugt `listForDayClose` (paid_at-Superset, damit heute
  // bezahlte Vorbestellungen mit zukünftigem Abholtag erfasst werden). Fällt
  // gestaffelt zurück: listForDayClose → listForRange (pickup-only) → Cache.
  // S136 Cut C — Schwund-Zeilen (order_corrections OHNE TSE) für den Buchungstag
  // best-effort frisch dazuladen. Eigener, getrennt ausgewiesener Z-Bon-Block —
  // NICHT in Umsatz/TSE-Summen gemischt. Fehlt der Adapter/wirft er → leer.
  let schwundRows = [];
  if (storage && typeof storage.listSchwund === 'function') {
    try {
      schwundRows = await storage.listSchwund({ date: dateIso });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[receipt] computeDayCloseReceiptFresh listSchwund fehlgeschlagen — Schwund-Block leer:', err);
    }
  }
  // S137 — Teilbelege (order_receipts) für den Buchungstag best-effort frisch dazu.
  // Pflicht für den Fresh-Pfad: der 30-Tage-Order-Cache kennt keine Receipts; ohne
  // sie fehlt der Split-Umsatz (Memory gg-export-fresh-db).
  let receiptRows = [];
  if (storage && typeof storage.listReceiptsForDayClose === 'function') {
    try {
      receiptRows = await storage.listReceiptsForDayClose(dateIso);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[receipt] computeDayCloseReceiptFresh listReceiptsForDayClose fehlgeschlagen — Split-Umsatz fehlt:', err);
    }
  }
  // S242/phase_173 — Teil-Storno-Belege (kind='refund') mindern den Umsatz und
  // gehören in den Korrektur-Block. Bis S241 landeten sie über `listSchwund`
  // im Warenverlust und minderten den Umsatz gar nicht (HF-11).
  let correctionBatches = [];
  if (storage && typeof storage.listCorrectionBatchesForDayClose === 'function') {
    try {
      correctionBatches = await storage.listCorrectionBatchesForDayClose(dateIso);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[receipt] computeDayCloseReceiptFresh listCorrectionBatchesForDayClose fehlgeschlagen — Teil-Stornos fehlen:', err);
    }
  }
  if (storage && typeof storage.listForDayClose === 'function') {
    try {
      const orders = await storage.listForDayClose(dateIso);
      return _computeDayCloseReceiptFromOrders(dateIso, orders, schwundRows, receiptRows, correctionBatches);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[receipt] computeDayCloseReceiptFresh listForDayClose fehlgeschlagen, fallback auf listForRange:', err);
    }
  }
  if (storage && typeof storage.listForRange === 'function') {
    try {
      const orders = await storage.listForRange(dateIso, dateIso);
      return _computeDayCloseReceiptFromOrders(dateIso, orders, schwundRows, receiptRows, correctionBatches);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[receipt] computeDayCloseReceiptFresh listForRange fehlgeschlagen, fallback auf Cache:', err);
    }
  } else if (!storage || typeof storage.listForDayClose !== 'function') {
    // eslint-disable-next-line no-console
    console.warn('[receipt] orders.listForDayClose/listForRange nicht verfügbar — fallback auf state-cache');
  }
  return computeDayCloseReceipt(dateIso);
}

export function computeDayCloseReceipt(dateIso) {
  const state = getState();
  const orders = Array.isArray(state.orders) ? state.orders : [];
  const all = orders.filter(o => dayCloseBookingDate(o) === dateIso);
  return _computeDayCloseReceiptDetails(dateIso, all);
}

/**
 * Interne Helper-Funktion: nimmt eine vorgefilterte Liste `all` (nur Orders
 * mit dem passenden pickupDate) und berechnet daraus alle Aggregate.
 * Extrahiert aus dem ursprünglichen `computeDayCloseReceipt`-Body in S43
 * für M-11. Identische Logik wie zuvor, nur die Quelle (`state.orders`
 * vs. DB-Fresh) wandert in die aufrufende Funktion.
 */
function _computeDayCloseReceiptDetails(dateIso, all, schwundRows, receipts, correctionBatches) {

  // S137 — Umsatz/USt/Bezahlmethoden sind seit P4 NICHT mehr order-, sondern
  // BELEG-basiert. „Ohne Beleg kein Umsatz" wird strukturell:
  //
  //   Beleg = { Voll-Zahlung: orders-Zeile MIT invoice_number (mark_order_paid,
  //             TSE auf der Order) }
  //         ∪ { Teilbeleg: order_receipts-Zeile (Split via pay_order_split,
  //             eigene invoice_number + payment_method + TSE) }
  //
  // Damit fallen automatisch raus (kein invoice / keine receipts):
  //   - offene/unbezahlte Orders,
  //   - Schwund-Orders (paid, aber method=NULL, invoice=NULL — der Umsatz steckt
  //     in keinem Beleg, weil alle Items abgeschrieben wurden),
  //   - die Split-Mutter-Order selbst (invoice=NULL; ihr Umsatz lebt in den receipts).
  //
  // Korrekturbelege (Storno, is_correction) tragen eine Stornobeleg-Nr in
  // `invoiceNumber` → bleiben im Beleg-Set, mit NEGATIVEN Beträgen → mindern den
  // Umsatz exakt (umsatzmindernd, MIT TSE — unverändert).
  const receiptList = Array.isArray(receipts) ? receipts : [];

  // S242/phase_173 — Teil-Storno-Belege. `kind='refund'` ist ein Kassenbeleg mit
  // eigener Belegnummer und TSE-Signatur; seine Beträge sind bereits NEGATIV
  // gespeichert und mindern damit den Umsatz wie ein Voll-Storno.
  // `kind='schwund'` ist Warenverlust und bleibt außerhalb des Umsatzes.
  const refundBatches = (Array.isArray(correctionBatches) ? correctionBatches : [])
    .filter(b => b.kind === 'refund');

  const belegOrders   = all.filter(o => o.invoiceNumber && !o.isCorrection
                                        && o.status !== 'cancelled' && o.status !== 'no_show'); // Voll-Zahlungen
  const corrections   = all.filter(o =>  o.isCorrection);                       // Stornobelege (negativ)
  const cancellations = all.filter(o => !o.isCorrection && o.status === 'cancelled'); // Storno VOR Bezahlung
  const noShows       = all.filter(o =>  o.status === 'no_show');

  // Beleg-Set für Umsatz/USt/Bezahlmethoden: Voll-Belege ∪ Teilbelege ∪
  // Voll-Stornos ∪ Teil-Storno-Belege.
  // Orders nutzen tax7Cents, Teilbelege tax7AmountCents → Reducer deckt beide ab.
  const revenueBelege = belegOrders.concat(receiptList, corrections, refundBatches);

  const grossCents = revenueBelege.reduce((s, o) => s + (Number(o.totalGrossCents) || 0), 0);
  const tax7Cents  = revenueBelege.reduce((s, o) => s + (Number(o.tax7Cents)  || Number(o.tax7AmountCents)  || 0), 0);
  const tax19Cents = revenueBelege.reduce((s, o) => s + (Number(o.tax19Cents) || Number(o.tax19AmountCents) || 0), 0);
  const netCents   = revenueBelege.reduce((s, o) => s + (Number(o.subtotalNetCents) || 0), 0);
  // S241 / HF-10 — Netto je Satz kam bisher aus `round(steuer / satz)`, also aus
  // genau der Rückrechnung, die im TSE-QR den 3–7-ct-Drift verursacht hat: die
  // Summe stimmt, die Bemessungsgrundlage je Satz nicht, und net7+net19 konnte
  // sogar vom Gesamt-Netto abweichen. Jetzt wird JE BELEG das Brutto pro Satz
  // bestimmt (Positionen wenn vorhanden, sonst Steuer-Fallback mit Residual-
  // Korrektur) und aufsummiert; Netto = Brutto − Steuer. Damit gilt
  // Σ Brutto je Satz == Tagesumsatz und net7+net19 == netCents.
  let gross7Cents = 0, gross19Cents = 0;
  for (const b of revenueBelege) {
    const s = grossPerRateFromOrder(b);
    gross7Cents  += s.gross7Cents;
    gross19Cents += s.gross19Cents;
  }
  const net7Cents  = gross7Cents  - tax7Cents;
  const net19Cents = gross19Cents - tax19Cents;

  // Korrektur-Summen separat für den eigenen Z-Bon-Block: Voll-Stornos UND
  // Teil-Storno-Belege (beide negativ).
  const correctionsGrossCents = corrections.concat(refundBatches)
    .reduce((s, o) => s + (Number(o.totalGrossCents) || 0), 0);

  // S136 Cut C — Schwund / Warenverlust (order_corrections OHNE TSE): EIGENER
  // Block, KEIN Umsatz, KEIN TSE → fließt NICHT in grossCents/tax/byMethod ein.
  // Kommt aus listSchwund (tse NULL) → NIE im Korrektur-Bucket (kein Doppel-Count).
  const schwund = Array.isArray(schwundRows) ? schwundRows : [];
  const countSchwund      = schwund.length;
  const schwundGrossCents = schwund.reduce((s, r) => s + (Number(r.refund_amount_cents) || 0), 0);
  const schwundItems = schwund.map(r => ({
    label:       _schwundCategoryLabel(r.reason_category),
    qty:         Number(r.qty_cancelled) || 0,
    amountCents: Number(r.refund_amount_cents) || 0,
    reason:      r.reason || ''
  }));

  // Bezahlmethoden-Splitting — JE BELEG aus dessen EIGENER payment_method
  // (Voll-Order bzw. Teilbeleg), NICHT aus der payment_method der Split-Mutter.
  // Belege haben immer eine method → `unpaid` bleibt strukturell leer (nur als
  // defensiver Eimer erhalten). Korrekturen mindern ihre Original-Methode (negativ).
  const byMethod = { cash: 0, card: 0, stripe: 0, unpaid: 0 };
  for (const b of revenueBelege) {
    const m = (b.paymentMethod && byMethod[b.paymentMethod] != null) ? b.paymentMethod : 'unpaid';
    byMethod[m] += (Number(b.totalGrossCents) || 0);
  }

  // Rechnungsnummern-Range über ALLE Belege (Voll-invoices ∪ Storno-Nrn ∪ Teilbeleg-invoices).
  const invoiceNumbers = belegOrders.concat(corrections)
    .map(o => o.invoiceNumber)
    .concat(receiptList.map(r => r.invoiceNumber))
    .concat(refundBatches.map(b => b.invoiceNumber))
    .filter(Boolean)
    .sort();

  // „Produktiv" = umsatztragende Orders: Voll-Belege + DISTINKTE Split-Mutter-
  // Orders (über order_receipts). Korrekturen zählen separat (countCorrections).
  const splitOrderIds = new Set(receiptList.map(r => r.orderId).filter(Boolean));
  const count = belegOrders.length + splitOrderIds.size;

  return {
    mode: 'day-close',
    date: dateIso,
    zBonNumber: computeZBonSequence(dateIso),
    count,
    countCorrections: corrections.length + refundBatches.length,
    countCancelled:   cancellations.length,
    countNoShow:      noShows.length,
    countSchwund,
    schwundGrossCents,
    schwundItems,
    grossCents,
    netCents,
    net7Cents,
    net19Cents,
    gross7Cents,
    gross19Cents,
    tax7Cents,
    tax19Cents,
    correctionsGrossCents,
    byMethod,
    invoiceFirst: invoiceNumbers[0] || null,
    invoiceLast:  invoiceNumbers[invoiceNumbers.length - 1] || null
  };
}

/** S137 — DB-reason_category → deutsches Label für den Schwund-Block (Spiegel
 *  der Single-Source `schwundCategoryLabel` in orders-split.js; hier inline
 *  gehalten, damit das Fiskal-Modul receipt.js dependency-frei bleibt). */
function _schwundCategoryLabel(dbValue) {
  switch (dbValue) {
    case 'customer_complaint': return 'Reklamation';
    case 'preparation_error':  return 'Fehlzubereitung';
    case 'wrong_billing':      return 'Fehleingabe';
    case 'spoilage':           return 'Verderb';
    case 'out_of_stock':       return 'Nicht vorrätig';
    // S242-HF — die beiden Kontext-Kategorien fehlten und fielen auf
    // „Sonstiges" durch (Cowork-Befund im S242-Retest). Sie entstehen
    // automatisch beim Storno/No-Show einer zubereiteten Order
    // (`autoSchwundCategory` in orders-split.js) und sind damit die
    // HÄUFIGSTEN Schwund-Gründe überhaupt. Labels wortgleich zu
    // SCHWUND_CONTEXT_LABELS dort.
    case 'storno_after_prep':  return 'Storno nach Zubereitung';
    case 'no_show':            return 'No-Show';
    default:                   return 'Sonstiges';
  }
}

// -----------------------------------------------------------------------------
// HTML-Renderer — modernes Web-Layout
// -----------------------------------------------------------------------------

/**
 * Der Belegkopf.
 *
 * @param {object} c    Betriebsangaben
 * @param {object} [o]  `{ logoSrc, noInlineScripts }`
 *
 * S254 — `logoSrc` für Aufrufer ohne App-State (Gast am Tisch).
 *
 * S255 (B2) — `noInlineScripts`: das `onerror` am Logo ist für den Browser ein
 * Skript. Im sandbox-iframe der Gast-Seite (bewusst OHNE `allow-scripts`, das
 * ist der Sinn der Isolation seit S230) blockt Chrome es und schreibt bei JEDEM
 * geöffneten Beleg „Blocked script execution in 'about:srcdoc'" in die Konsole.
 * Ein Sicherheitsnetz, das im Zielkontext gar nicht laufen kann, ist keines —
 * es lärmt nur. Wer die Option setzt, entscheidet den Rückfall VOR dem Bauen
 * und übergibt eine Quelle, die trägt.
 */
function renderHeader(c, o) {
  const opts = (o && typeof o === 'object') ? o : { logoSrc: o };
  const addr = companyAddressLine(c);
  // Logo (Phase 31, Session 29): oben mittig auf jedem Beleg. Auf dem
  // 80mm-Druckpfad max 60×40mm, in der Web-Card max 120×80px. Bei
  // fehlendem Custom-Logo greift Default `assets/logo.png` aus dem Repo.
  const logoSrc = htmlEscape(opts.logoSrc || getBrandLogoSrc());
  const onErr = opts.noInlineScripts
    ? ''
    : ` onerror="this.onerror=null; this.src='${htmlEscape(DEFAULT_LOGO_URL)}';"`;
  return `
    <header class="receipt-header">
      <img class="receipt-logo"
           src="${logoSrc}"
           alt="${htmlEscape(c.brand || 'Logo')}"${onErr}>
      <div class="receipt-brand">${htmlEscape(c.brand)}</div>
      <div class="receipt-legal-name">${htmlEscape(c.name)}</div>
      ${addr ? `<div class="receipt-addr">${htmlEscape(addr)}</div>` : ''}
      <div class="receipt-meta-row">
        ${c.phone ? `<span>Tel ${htmlEscape(c.phone)}</span>` : ''}
        ${c.email ? `<span>${htmlEscape(c.email)}</span>` : ''}
      </div>
      <div class="receipt-tax-id">${htmlEscape(taxIdLine(c))}</div>
    </header>
  `;
}

function renderTaxBreakdown(data) {
  // 7% und 19% nur anzeigen, wenn relevant (Beträge != 0; bei Storno negativ)
  const rows = [];
  if (data.net7Cents !== 0 || data.tax7Cents !== 0) {
    rows.push(`
      <tr>
        <td>Netto 7 %</td>
        <td class="num">${centsToEur(data.net7Cents)} €</td>
        <td>MwSt 7 %</td>
        <td class="num">${centsToEur(data.tax7Cents)} €</td>
      </tr>
    `);
  }
  if (data.net19Cents !== 0 || data.tax19Cents !== 0) {
    rows.push(`
      <tr>
        <td>Netto 19 %</td>
        <td class="num">${centsToEur(data.net19Cents)} €</td>
        <td>MwSt 19 %</td>
        <td class="num">${centsToEur(data.tax19Cents)} €</td>
      </tr>
    `);
  }
  if (rows.length === 0) {
    rows.push(`<tr><td colspan="4" class="receipt-empty">Keine umsatzsteuerpflichtigen Positionen.</td></tr>`);
  }
  return `
    <table class="receipt-tax-table">
      <tbody>${rows.join('')}</tbody>
    </table>
  `;
}

/**
 * TSE-Block für eine Order. Liefert leer-string, wenn die Order keine
 * TSE-Daten hat (z.B. weil im Mode 'disabled' bezahlt wurde).
 *
 * Phase 10a (Session 14): Klartext-Block.
 * Session 54: visueller SVG-QR-Code oben (BSI TR-03153-konform für Steuer-
 * prüfer-Scan), Klartext als <details>-Akkordeon als Audit-Fallback.
 *
 * Render-Strategie:
 *   1. Synchrones renderTseBlock() liefert HTML mit Placeholder
 *      `<div class="receipt-tse-qr" data-qr-payload="...">SVG kommt...</div>`.
 *   2. attachReceiptTseQr(rootEl) wird vom Caller (receipt-modal.js etc.)
 *      NACH dem Einfügen ins DOM aufgerufen und ersetzt den Placeholder
 *      durch den echten SVG-QR (async, da qrcode-generator lazy-loaded).
 *   3. Wenn der Caller das vergisst: kein Drama, der Klartext-Block bleibt
 *      sichtbar und der Bon ist immer noch KassenSichV-konform.
 */
export function renderTseBlock(order) {
  if (!order || !order.tseTransactionId) return '';
  const mode = (order.tseMode || 'mock').toLowerCase();
  const isMock = mode === 'mock';
  const signedAt = order.tseSignedAt
    ? new Date(order.tseSignedAt).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      })
    : '—';
  const counter = (order.tseSignatureCounter != null) ? String(order.tseSignatureCounter) : '—';
  const payload = order.tseQrPayload || '';
  const payloadAttr = payload ? ` data-qr-payload="${htmlEscape(payload)}"` : '';
  return `
    <div class="receipt-tse receipt-tse-${isMock ? 'mock' : 'live'}">
      <div class="receipt-tse-headline">
        <span class="receipt-tse-badge">TSE</span>
        ${isMock
          ? 'TSE-Mock-Modus — <strong>nicht KassenSichV-konform</strong>. Bon zu Test- und Vorschauzwecken.'
          : 'Beleg gemäß §&nbsp;6 KassenSichV durch Technische Sicherheits&shy;einrichtung signiert.'}
      </div>
      ${payload ? `
        <div class="receipt-tse-qr"${payloadAttr} aria-label="TSE-QR-Code zum Scannen">
          <div class="receipt-tse-qr-placeholder" aria-hidden="true">QR-Code wird gerendert…</div>
        </div>
        <div class="receipt-tse-qr-hint">QR-Code zum Scannen durch die Finanzverwaltung (BSI&nbsp;TR-03153).</div>
      ` : ''}
      <table class="receipt-tse-table">
        <tbody>
          <tr><th>Transaktion</th><td>${htmlEscape(order.tseTransactionId)}</td></tr>
          <tr><th>Signaturzähler</th><td>${htmlEscape(counter)}</td></tr>
          <tr><th>Zeitstempel</th><td>${htmlEscape(signedAt)}</td></tr>
          <tr><th>Modus</th><td>${htmlEscape(mode)}</td></tr>
        </tbody>
      </table>
      ${payload ? `
        <details class="receipt-tse-payload-details">
          <summary>Klartext-Payload für Audit anzeigen</summary>
          <pre class="receipt-tse-payload" aria-label="TSE-QR-Code-Payload">${htmlEscape(payload)}</pre>
        </details>
      ` : ''}
      <div class="receipt-tse-hint">
        Aufbewahrungspflichtig 10 Jahre gemäß §&nbsp;147 AO.
      </div>
    </div>
  `;
}

/**
 * Sucht alle `.receipt-tse-qr[data-qr-payload]`-Container im übergebenen
 * Wurzel-Element und ersetzt deren Placeholder durch ein echtes SVG-QR.
 *
 * Aufrufer (z.B. Beleg-Aktion-Modal, Print-Preview) muss diese Funktion
 * NACH dem DOM-Einfügen aufrufen. Async, weil qrcode-generator lazy-
 * loaded ist. Idempotent: bereits gerenderte QR-Container (data-qr-rendered)
 * werden übersprungen.
 *
 * @param {ParentNode} rootEl Container, in dem nach .receipt-tse-qr gesucht wird.
 *                            Default: document.
 */
export async function attachReceiptTseQr(rootEl) {
  const root = rootEl || (typeof document !== 'undefined' ? document : null);
  if (!root || typeof root.querySelectorAll !== 'function') return;

  const targets = root.querySelectorAll('.receipt-tse-qr[data-qr-payload]:not([data-qr-rendered])');
  if (!targets || targets.length === 0) return;

  for (const el of targets) {
    const payload = el.getAttribute('data-qr-payload') || '';
    if (!payload) continue;
    try {
      const svg = await generateQrSvg(payload, {
        cellSize: 4, margin: 2, errorLevel: 'M',
        ariaLabel: 'TSE-QR-Code'
      });
      el.innerHTML = svg;
      el.setAttribute('data-qr-rendered', '1');
    } catch (err) {
      // Fail-soft: Placeholder bleibt, Klartext im <details> ist sichtbar.
      console.warn('[receipt] QR-Render fehlgeschlagen:', err);
    }
  }
}

// Backward-Compat: window-Export für Caller, die noch nicht ESM-importen
if (typeof window !== 'undefined') {
  window.receiptAttachTseQr = attachReceiptTseQr;
}

/**
 * Footer-Renderer. Akzeptiert optional die Order, damit der TSE-Block für
 * Einzelbestellungen gerendert werden kann. Im Day-Close-Modus (ohne Order)
 * zeigt der Footer einen aggregierten TSE-Hinweis (Einzelbelege sind
 * jeweils TSE-signiert).
 */
function renderFooter(extra, order, opts) {
  const isDayClose = !!(opts && opts.isDayClose);
  const tseBlock = renderTseBlock(order);
  // S92 / Befund A: Der Z-Bon (Tagesabschluss) ist nach KassenSichV / § 146a AO
  // eine **Tagesauswertung (Kassenbericht)** und trägt selbst KEINE TSE-Signatur —
  // signiert werden ausschließlich die einzelnen Kassenbelege (Transaktionen). Der
  // frühere „vorläufiger Beleg"-Hinweis war hier fachlich irreführend (suggerierte
  // einen Mangel, wo regelkonform gar keine Signatur hingehört). Beim Z-Bon zeigen
  // wir daher einen korrekten Einordnungs-Hinweis; nur beim Einzelbeleg OHNE
  // Signatur bleibt der „vorläufiger Beleg"-Hinweis bestehen (dort ist er korrekt).
  let tseFootnote = '';
  if (isDayClose) {
    tseFootnote = `
      <div class="receipt-tse">
        <span class="receipt-tse-badge" title="Technische Sicherheitseinrichtung">TSE</span>
        Tagesabschluss (Kassenbericht) — die einzelnen Kassenbelege sind gemäß
        §&nbsp;146a AO TSE-signiert. Der Z-Bericht selbst ist eine Tagesauswertung
        und trägt regelkonform keine eigene TSE-Signatur.
      </div>`;
  } else if (!tseBlock) {
    tseFootnote = `
      <div class="receipt-tse receipt-tse-pending">
        <span class="receipt-tse-badge" title="Technische Sicherheitseinrichtung">TSE</span>
        Keine TSE-Signatur vorhanden — Beleg ist als <em>vorläufiger Beleg</em>
        einzustufen. Aufbewahrungspflichtig gemäß §&nbsp;147 AO.
      </div>`;
  }
  return `
    <footer class="receipt-footer">
      ${tseBlock}
      ${tseFootnote}
      <div class="receipt-disclaimer">
        Diese Quittung wurde elektronisch erstellt und ist auch ohne
        Unterschrift gültig.
      </div>
      ${extra ? `<div class="receipt-extra">${extra}</div>` : ''}
      <div class="receipt-print-meta">Druck: ${htmlEscape(fmtNow())}</div>
      <div class="receipt-brand-sig" aria-label="Kassensystem-Anbieter">
        <span class="receipt-brand-sig-label">Kassensystem</span>
        <strong>YOUR GROWTH 365</strong>
      </div>
    </footer>
  `;
}

// -----------------------------------------------------------------------------
// renderOrderReceipt — Kassenbon einer Einzelbestellung
// -----------------------------------------------------------------------------

/**
 * Der Kassenbon einer Bestellung.
 *
 * @param {object} order
 * @param {object} [opts] S254 — `{ company, logoSrc }` für Aufrufer ohne
 *        `window.App.storage`-State. Ohne `opts` unverändertes Verhalten;
 *        Archiv, PDF, Mail und Druck im Cockpit rufen weiterhin einstellig.
 */
export function renderOrderReceipt(order, opts) {
  const r = computeOrderReceipt(order);
  if (!r) return '<div class="receipt-empty">Keine Bestelldaten.</div>';
  const c = getCompanyHeader(opts && opts.company);
  const logoSrc = (opts && opts.logoSrc) || null;
  const isCorr = !!r.isCorrection;

  // Phase 10a.2: Original-Order, die einen Stornobeleg bekommen hat — wir
  // suchen den Stornobeleg im Cache, um seine Belegnummer auf dem Original-Bon
  // anzeigen zu können (operativer Hinweis, nicht steuerlich relevant).
  let stornoNote = '';
  if (!isCorr && order && order.correctedByOrderId) {
    const all = getState().orders || [];
    const stornoOrder = all.find(o => o.id === order.correctedByOrderId);
    const refNo = stornoOrder ? (stornoOrder.invoiceNumber || stornoOrder.orderNumber) : order.correctedByOrderId;
    stornoNote = `<div class="receipt-corrected-banner">Storniert durch Korrekturbeleg <strong>${htmlEscape(refNo)}</strong></div>`;
  }

  // S169-Korrektur — KEINE Deal-Sonderdarstellung mehr: ein Deal-Hauptgericht ist
  // ein normales order_item (Beilage in `side`, Drink separate Zeile). Der Bon
  // rendert es wie jede Normalbestellung — kein „Aktion"-Kopf, keine Einrückung.
  const itemRows = r.items.map((it) => {
    const name = (it && (it.displayName || it.name || it.title)) || '(unbenannt)';
    const qty  = Number(it && it.qty) || 1;
    const unit = Number(it && (it.unit_price_cents || it.unitPriceCents)) || 0;
    const total = Number(it && (it.line_total_cents || it.totalCents)) || (unit * qty);
    const note = (it && (it.note || it.specialRequests)) || '';
    const displayTotal = isCorr ? -Math.abs(total) : total;

    // S57 iter9: kompaktes 4-Spalten-Layout. MwSt-Info wandert komplett in
    // den Summen-Block unten (KassenSichV-konform via § 33 UStDV, Kleinbetrags-
    // rechnung). Position-Spalte zeigt nur Name + ggf. Notiz — bleibt schmal.
    return `
      <tr>
        <td class="num receipt-item-qty">${isCorr ? '-' : ''}${qty}×</td>
        <td>
          <div class="receipt-item-name">${htmlEscape(name)}</div>
          ${note ? `<div class="receipt-item-note">${htmlEscape(note)}</div>` : ''}
        </td>
        <td class="num">${centsToEur(unit)}</td>
        <td class="num">${centsToEur(displayTotal)}</td>
      </tr>
    `;
  }).join('');

  // S143 / Ticket 1 — Liefergebühr als eigene Positionszeile (delivery mit Gebühr),
  // damit die Summe der Positionen == Brutto ist. Beträge stecken serverseitig schon
  // in den Order-Cent-Spalten (phase_109) — hier nur die Anzeige. Bei Stornobeleg negativ.
  const feeInfo = deliveryFeeReceiptInfo(order);
  const feeRow = feeInfo ? `
      <tr>
        <td class="num receipt-item-qty"></td>
        <td>
          <div class="receipt-item-name">Liefergebühr <span style="color:var(--ink-soft);font-weight:normal;">(${feeInfo.taxRate} % USt)</span></div>
        </td>
        <td class="num"></td>
        <td class="num">${centsToEur(isCorr ? -Math.abs(feeInfo.feeCents) : feeInfo.feeCents)}</td>
      </tr>
    ` : '';

  const belegLabel = isCorr ? 'Stornobeleg-Nr' : (r.invoiceNumber ? 'Rechnungs-Nr' : 'Beleg-Nr');
  const belegnr = r.invoiceNumber
    ? `${belegLabel} ${htmlEscape(r.invoiceNumber)}`
    : `${belegLabel} ${htmlEscape(r.orderNumber)}${isCorr ? '' : ' (vorläufig)'}`;

  // Bei Korrekturbeleg: Verweis aufs Original ist Pflicht-Angabe
  let correctionMetaBlock = '';
  if (isCorr) {
    const all = getState().orders || [];
    const orig = all.find(o => o.id === r.correctsOrderId);
    const origRef = orig ? (orig.invoiceNumber || orig.orderNumber) : (r.correctsOrderId || '—');
    const origDate = orig ? fmtDateDE(orig.pickupDate) : '';
    const reasonLabel = CORRECTION_REASON_LABEL[r.correctionReasonCode] || r.correctionReasonCode || '—';
    correctionMetaBlock = `
      <section class="receipt-correction-meta">
        <div class="receipt-correction-headline">Stornobeleg / Korrekturbeleg</div>
        <div class="receipt-correction-grid">
          <div><span class="receipt-label">Bezieht sich auf</span><span class="receipt-value">${htmlEscape(origRef)}${origDate ? ' · ' + htmlEscape(origDate) : ''}</span></div>
          <div><span class="receipt-label">Grund</span><span class="receipt-value">${htmlEscape(reasonLabel)}</span></div>
          ${r.correctionReasonText ? `<div class="receipt-correction-text"><span class="receipt-label">Bemerkung</span><span class="receipt-value">${htmlEscape(r.correctionReasonText)}</span></div>` : ''}
        </div>
        <div class="receipt-correction-hint">
          Dieser Beleg neutralisiert die Original-Buchung in voller Höhe.
          Aufbewahrungspflichtig 10 Jahre gemäß §&nbsp;147 AO.
        </div>
      </section>
    `;
  }

  // Bezahl-Zeile: bei Korrektur «Rückerstattet am …»
  let paymentLine;
  if (isCorr) {
    paymentLine = r.paidAt
      ? `<strong>Rückerstattet am</strong> ${htmlEscape(new Date(r.paidAt).toLocaleString('de-DE', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}))} · ${htmlEscape(PAYMENT_LABEL[r.paymentMethod] || r.paymentMethod || '—')}`
      : '<strong>Rückerstattung wird verbucht</strong>';
  } else if (r.paymentMethod) {
    paymentLine = `Bezahlt am ${htmlEscape(new Date(r.paidAt).toLocaleString('de-DE', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}))} · ${htmlEscape(PAYMENT_LABEL[r.paymentMethod] || r.paymentMethod)}`;
  } else {
    paymentLine = '<strong style="color:var(--accent);">Noch nicht bezahlt</strong>';
  }

  // S179 R3a — Rabatt-Zeile: wenn ein Rabatt angewandt wurde, zeigt der Bon die
  // Zwischensumme (Brutto VOR Rabatt = gemindertes Brutto + Rabattbetrag) und eine
  // eigene Rabatt-Zeile. Der MwSt-Block + „Gesamt brutto" stehen bereits auf dem
  // gemminderten (signierten) Betrag — Single-Source aus den Order-Cent-Spalten.
  const discCents = Number((order && order.discountCents) || 0);
  let discountBlock = '';
  if (!isCorr && discCents > 0) {
    const dName = (order && order.discountName) || 'Rabatt';
    const dPct  = (order && order.discountPercent != null) ? String(order.discountPercent).replace('.', ',') : null;
    const grossBefore = (Number(r.grossCents) || 0) + discCents;
    discountBlock = `
        <div class="receipt-total-row receipt-subtotal-row">
          <span class="receipt-total-label">Zwischensumme</span>
          <span class="receipt-total-value">${centsToEur(grossBefore)} €</span>
        </div>
        <div class="receipt-total-row receipt-discount-row">
          <span class="receipt-total-label">${htmlEscape(dName)}${dPct ? ` (−${dPct} %)` : ''}</span>
          <span class="receipt-total-value">−${centsToEur(discCents)} €</span>
        </div>`;
  }

  return `
    <article class="receipt receipt-order${isCorr ? ' receipt-correction' : ''}">
      ${renderHeader(c, { logoSrc, noInlineScripts: !!(opts && opts.noInlineScripts) })}
      ${isCorr ? '<div class="receipt-correction-stamp">STORNOBELEG</div>' : ''}
      ${stornoNote}
      <section class="receipt-meta">
        <div class="receipt-meta-grid">
          <div><span class="receipt-label">Beleg</span><span class="receipt-value">${belegnr}</span></div>
          <div><span class="receipt-label">Bestellung</span><span class="receipt-value">${htmlEscape(r.orderNumber)}</span></div>
          ${(() => {
            // S255 (B1) — dieselbe Regel wie bei der Kunden-Zeile darunter:
            // lieber weglassen als eine beschriftete Zeile mit nichts als einem
            // Mittelpunkt darin. Im Gast-Beleg stand genau das, weil
            // `guest_receipt_view` weder Abholdatum noch -zeit kennt.
            const dat = fmtDateDE(r.order.pickupDate);
            const zeit = fmtTime(r.order.pickupTime);
            if (!dat && !zeit) return '';
            const wert = [dat, zeit].filter(Boolean).join(' · ');
            return `<div><span class="receipt-label">Datum</span><span class="receipt-value">${wert}</span></div>`;
          })()}
          ${(() => {
            // S134c (Befund 3) — Kunden-Zeile nur bei echtem Namen (kein
            // „Theke 2026-…"-Platzhalter); sonst weglassen statt leer rendern.
            const cn = displayGuestName(r.order.customerName);
            return cn ? `<div><span class="receipt-label">Kunde</span><span class="receipt-value">${htmlEscape(cn)}</span></div>` : '';
          })()}
        </div>
      </section>

      ${correctionMetaBlock}

      <section class="receipt-items">
        <table class="receipt-items-table">
          <colgroup>
            <col style="width:13%">
            <col>
            <col style="width:20%">
            <col style="width:20%">
          </colgroup>
          <thead>
            <tr>
              <th class="th-center">Menge</th>
              <th class="th-center">Pos.</th>
              <th class="th-center">EP</th>
              <th class="th-center">Gesamt</th>
            </tr>
          </thead>
          <tbody>${(itemRows + feeRow) || '<tr><td colspan="4" class="receipt-empty">Keine Positionen.</td></tr>'}</tbody>
        </table>
      </section>

      <section class="receipt-totals">
        ${discountBlock}
        ${renderTaxBreakdown(r)}
        <div class="receipt-total-row">
          <span class="receipt-total-label">${isCorr ? 'Rückerstattung gesamt' : 'Gesamt brutto'}</span>
          <span class="receipt-total-value">${centsToEur(r.grossCents)} €</span>
        </div>
        <div class="receipt-pay-line">${paymentLine}</div>
      </section>

      ${renderFooter('', r.order)}
    </article>
  `;
}

// -----------------------------------------------------------------------------
// renderDayCloseReceipt — Z-Bon Tagesabschluss
// -----------------------------------------------------------------------------

/**
 * Async-Variante des Z-Bon-Renderers für M-11 (Session 43):
 * holt die Orders frisch aus der DB statt aus dem 30-Tage-Cache, sodass
 * Z-Bons für historische Daten ältere als 30 Tage korrekte Beträge zeigen.
 *
 * Verwendung: View-Layer (`day-close.js loadAndRenderDayClose`); der
 * Print-Pfad (`printDayCloseReceipt`) bleibt auf der sync-Variante,
 * weil dort die Daten bereits aus dem aktuellen Render-Lauf stammen.
 */
export async function renderDayCloseReceiptFresh(dateIso) {
  const r = await computeDayCloseReceiptFresh(dateIso);
  return _renderDayCloseReceiptFromData(dateIso, r);
}

export function renderDayCloseReceipt(dateIso) {
  const r = computeDayCloseReceipt(dateIso);
  return _renderDayCloseReceiptFromData(dateIso, r);
}

/**
 * Pure-Render-Helper (Session 43, M-11): nimmt den vorberechneten Z-Bon-
 * Datensatz `r` und baut das HTML. Wird sowohl vom sync-Pfad
 * (`renderDayCloseReceipt`) als auch vom async-Pfad
 * (`renderDayCloseReceiptFresh`) genutzt.
 */
function _renderDayCloseReceiptFromData(dateIso, r) {
  const c = getCompanyHeader();

  if (r.count === 0 && r.countCancelled === 0 && r.countNoShow === 0
      && r.countCorrections === 0 && (r.countSchwund || 0) === 0) {
    return `
      <article class="receipt receipt-day-close receipt-empty-state">
        ${renderHeader(c)}
        <div class="receipt-empty">
          Für <strong>${fmtDateDE(dateIso)}</strong> gibt es noch keine
          produktiven Bestellungen.
        </div>
      </article>
    `;
  }

  const invoiceRange = (r.invoiceFirst && r.invoiceLast)
    ? (r.invoiceFirst === r.invoiceLast ? r.invoiceFirst : r.invoiceFirst + ' .. ' + r.invoiceLast)
    : '—';

  return `
    <article class="receipt receipt-day-close">
      ${renderHeader(c)}

      <section class="receipt-meta">
        <div class="receipt-meta-grid">
          <div><span class="receipt-label">Beleg</span><span class="receipt-value">Z-Bon ${htmlEscape(r.zBonNumber)}</span></div>
          <div><span class="receipt-label">Datum</span><span class="receipt-value">${fmtDateDE(r.date)}</span></div>
          <div><span class="receipt-label">Bestellungen</span><span class="receipt-value">${r.count} produktiv</span></div>
          <div><span class="receipt-label">Rechnungen</span><span class="receipt-value">${htmlEscape(invoiceRange)}</span></div>
        </div>
      </section>

      <section class="receipt-overview">
        <div class="receipt-overview-card">
          <div class="receipt-overview-label">Produktiv</div>
          <div class="receipt-overview-value">${r.count}</div>
        </div>
        <div class="receipt-overview-card receipt-overview-card-correction">
          <div class="receipt-overview-label">Korrekturbelege</div>
          <div class="receipt-overview-value">${r.countCorrections}</div>
          ${r.countCorrections > 0
            ? `<div class="receipt-overview-sub">${centsToEur(r.correctionsGrossCents)} €</div>`
            : ''}
        </div>
        <div class="receipt-overview-card receipt-overview-card-muted">
          <div class="receipt-overview-label">Stornos vor Bezahlung</div>
          <div class="receipt-overview-value">${r.countCancelled}</div>
        </div>
        <div class="receipt-overview-card receipt-overview-card-muted">
          <div class="receipt-overview-label">No-Shows</div>
          <div class="receipt-overview-value">${r.countNoShow}</div>
        </div>
      </section>

      <section class="receipt-totals">
        ${renderTaxBreakdown(r)}
        <div class="receipt-total-row">
          <span class="receipt-total-label">Gesamt brutto</span>
          <span class="receipt-total-value">${centsToEur(r.grossCents)} €</span>
        </div>
      </section>

      <section class="receipt-payment">
        <h4>Bezahlmethoden (je Beleg)</h4>
        <table class="receipt-payment-table">
          <tbody>
            <tr><td>Bar</td>                <td class="num">${centsToEur(r.byMethod.cash)} €</td></tr>
            <tr><td>EC / Kreditkarte</td>   <td class="num">${centsToEur(r.byMethod.card)} €</td></tr>
            <tr><td>Online (Stripe)</td>    <td class="num">${centsToEur(r.byMethod.stripe)} €</td></tr>
            ${(r.byMethod.unpaid || 0) !== 0
              ? `<tr class="receipt-payment-unpaid"><td>Ohne Zahlart</td><td class="num">${centsToEur(r.byMethod.unpaid)} €</td></tr>`
              : ''}
          </tbody>
        </table>
      </section>

      ${(r.countSchwund || 0) > 0 ? `
      <section class="receipt-payment receipt-schwund">
        <h4>Schwund / Warenverlust (kein Umsatz, kein TSE)</h4>
        <table class="receipt-payment-table">
          <tbody>
            ${(r.schwundItems || []).map(s => {
              // S137b — Freitext-Reason nur, wenn er ECHTE Zusatzinfo trägt: ein
              // führendes „Schwund:"-Präfix (Bestandsdaten) strippen, dann nur
              // zeigen, wenn der Rest nicht leer und nicht == Kategorie-Label ist.
              const extra = String(s.reason || '').replace(/^\s*schwund:\s*/i, '').trim();
              const showReason = extra && extra.toLowerCase() !== String(s.label || '').toLowerCase();
              return `
            <tr>
              <td>${htmlEscape(s.label)}${s.qty ? ' · ' + s.qty + '×' : ''}${showReason ? ' — ' + htmlEscape(extra) : ''}</td>
              <td class="num">${centsToEur(s.amountCents)} €</td>
            </tr>`;
            }).join('')}
            <tr class="receipt-total-row"><td>Summe Warenverlust</td><td class="num">${centsToEur(r.schwundGrossCents)} €</td></tr>
          </tbody>
        </table>
        <div class="receipt-disclaimer">Interner Warenverlust — getrennt vom fiskalischen Umsatz ausgewiesen, nicht TSE-signiert.</div>
      </section>` : ''}

      ${renderFooter('', null, { isDayClose: true })}
    </article>
  `;
}

// -----------------------------------------------------------------------------
// Print-Helper — öffnet ein Print-Window mit isoliertem Inhalt
// -----------------------------------------------------------------------------

/**
 * Liest die globale Bondrucker-Breite aus den Settings.
 * Default '80mm'. Werte: '58mm' | '80mm'.
 * Wird Per-Drucker-paperWidthMm (ESC/POS) bewusst NICHT überschreiben — dies
 * ist ausschließlich für den Browser-Print (window.print) gedacht.
 */
export function getReceiptPrinterWidth() {
  const s = getState().settings || {};
  const v = String(s.bonPrinterWidth || '80mm').toLowerCase();
  return v === '58mm' ? '58mm' : '80mm';
}

/**
 * Setzt/aktualisiert ein dynamisch erzeugtes <style>-Tag im <head>, das die
 * korrekte @page-Größe für den nächsten window.print()-Aufruf trägt.
 * Browser bewerten @page-Regeln nur global, nicht per CSS-Klasse — daher
 * dieser JS-Pfad. Beim Wechsel der Breite wird das Tag überschrieben.
 */
function applyPagePrintSize(width) {
  const id = 'gg-receipt-page-size';
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = id;
    tag.setAttribute('media', 'print');
    document.head.appendChild(tag);
  }
  const size   = width === '58mm' ? '58mm auto' : '80mm auto';
  const margin = width === '58mm' ? '2mm'       : '3mm';
  tag.textContent = `@page { size: ${size}; margin: ${margin}; }`;
}

/**
 * Druckt einen Bon ohne den Rest des Cockpit-DOMs zu beeinflussen.
 * Wir nutzen kein iframe-Printing (Browser-übergreifend zickig), sondern
 * setzen einen body-Klassen-Marker, der via @media print das Layout
 * umstellt. Vor dem Druck rendern wir den gewünschten Bon in ein
 * verstecktes #receipt-print-target und triggern window.print.
 *
 * Phase 11b: Setting bonPrinterWidth steuert zusätzlich die @page-Size
 * (via dynamisch injiziertem <style media="print">) und eine zweite
 * Body-Klasse 'printing-receipt-58mm', die das Layout enger macht.
 */
export function printReceiptHtml(html) {
  let target = document.getElementById('receipt-print-target');
  if (!target) {
    target = document.createElement('div');
    target.id = 'receipt-print-target';
    document.body.appendChild(target);
  }
  target.innerHTML = html;
  const width = getReceiptPrinterWidth();
  applyPagePrintSize(width);
  document.body.classList.add('printing-receipt');
  if (width === '58mm') {
    document.body.classList.add('printing-receipt-58mm');
  } else {
    document.body.classList.remove('printing-receipt-58mm');
  }
  const cleanup = () => {
    document.body.classList.remove('printing-receipt');
    document.body.classList.remove('printing-receipt-58mm');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);

  // S54 — TSE-QR-Code im Print-Target rendern, BEVOR der Browser-Print
  // ausgelöst wird. Sonst zeigt der Druck nur den "QR-Code wird gerendert…"
  // Placeholder (Funktionstest-Befund). attachReceiptTseQr ist async und
  // lädt qrcode-generator lazy — daher kurzer await vor window.print().
  (async () => {
    try {
      await attachReceiptTseQr(target);
    } catch (qrErr) {
      console.warn('[printReceiptHtml] QR-Render im Print-Target fehlgeschlagen:', qrErr);
    }
    // Kurzes Defer, damit das DOM nach dem SVG-Ersatz reflowed ist bevor
    // der Browser die Print-Vorschau aufnimmt.
    setTimeout(() => window.print(), 50);
  })();
}

export function printOrderReceipt(order) {
  printReceiptHtml(renderOrderReceipt(order));
}

export function printDayCloseReceipt(dateIso) {
  printReceiptHtml(renderDayCloseReceipt(dateIso || todayISO()));
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderOrderReceipt,
    renderDayCloseReceipt,
    computeOrderReceipt,
    computeDayCloseReceipt,
    printOrderReceipt,
    printDayCloseReceipt,
    printReceiptHtml,
    getReceiptPrinterWidth
  });
}
